import React from "react";
import styled, { Interpolation, useTheme } from "styled-components";

import { HexTooltip } from "../../../hex-components";
import { AttentionIcon } from "../../icons/CustomIcons";

import { InputFormLabel } from "./InputFormLabel";

export const BaseInputConfigurationContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const FormLabelContainer = styled.div`
  flex: none;
  margin-right: 8px;
  padding: 5px 0px;
`;

const InputContainer = styled.div`
  position: relative;

  flex: auto;
  min-width: 0;
`;

const StyledIconContainer = styled.div<{
  iconStyles?: Interpolation<object>;
}>`
  position: absolute;
  top: 6px;
  right: 5px;
  display: flex;
  gap: 3px;
  align-items: center;

  ${({ iconStyles }) => iconStyles}
`;

export interface BaseInputConfigurationProps {
  label?: string | React.ReactNode;
  labelForId?: string;
  info?: string;
  infoLocation?: "tooltip" | "inline";
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  isRequired?: boolean;
  error?: string;
  iconStyles?: Interpolation<object>;
  icon?: React.ReactNode;
}

export const BaseInputConfiguration: React.ComponentType<BaseInputConfigurationProps> =
  React.memo(function BaseInputConfiguration({
    children,
    className,
    disabled,
    error,
    icon,
    iconStyles,
    info,
    infoLocation,
    isRequired,
    label,
    labelForId,
  }) {
    const theme = useTheme();
    return (
      <BaseInputConfigurationContainer className={className}>
        {label != null && (
          <FormLabelContainer>
            <InputFormLabel
              disabled={disabled}
              info={info}
              infoLocation={infoLocation}
              isRequired={isRequired}
              label={label}
              labelForId={labelForId}
            />
          </FormLabelContainer>
        )}
        <InputContainer>
          {children}
          {(error || icon) && (
            <StyledIconContainer iconStyles={iconStyles}>
              {error && (
                <HexTooltip
                  autoFocus={false}
                  content={error}
                  css="margin-top: -2px;"
                  disabled={error == null || disabled}
                  enforceFocus={false}
                  openOnTargetFocus={false}
                  placement="right"
                >
                  <AttentionIcon color={theme.intent.DANGER} />
                </HexTooltip>
              )}
              {icon}
            </StyledIconContainer>
          )}
        </InputContainer>
      </BaseInputConfigurationContainer>
    );
  });
